import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { Title, Fieldset, Button, Control, Field } from "rbx";
import { useMutation, useApolloClient } from "react-apollo";

import {
  CREATE_INVENTORY_STORAGE_DIM_MUTATION,
  UPDATE_INVENTORY_STORAGE_DIM_MUTATION,
  DELETE_INVENTORY_STORAGE_DIM_MUTATION
} from "../../graphql/InventoryStorageDims/InventoryStorageDims.graphql";

import { ALL_ITEM_TRANSACTIONS_QUERY } from "../../graphql/Items/ItemTransactions.graphql";
import { ourToast as toast } from "../../utils";

import InventoryStorageDimForm from "./InventoryStorageDimForm";
import { ModalContext } from "../ModalProvider";
import { Confirmation } from "../modals";

const DEFAULT_STATE = {
  Active: true,
  CompanyLocRecIdRef: null,
  InvStorageDimRecIdRef: null
};

const AddInventoryStorageDimForm = ({ onComplete, data }) => {
  const [inputs, setInputs] = useState({ ...DEFAULT_STATE });
  const [editing, setEditing] = useState(false);
  const client = useApolloClient();
  const { setModalOpen } = useContext(ModalContext);

  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    if (data && data.RecId) {
      setInputs({
        DimCode: data.DimCode,
        DimDescription: data.DimDescription,
        InvStorageDimRecIdRef: data.InvStorageDimRecIdRef,
        Active: data.Active,
        CompanyLocRecIdRef: parseInt(data.CompanyLocRecIdRef)
      });
      setEditing(true);
    }
  }, [data]);

  const handleChange = e => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setInputs({ ...inputs, [e.target.name]: value });
  };

  const [createInventoryStorageDim, { loading }] = useMutation(
    CREATE_INVENTORY_STORAGE_DIM_MUTATION,
    {
      refetchQueries: [
        "ALL_INVENTORY_STORAGE_DIMS_QUERY",
        "SINGLE_INVENTORY_STORAGE_DIM_QUERY"
      ]
    }
  );
  const [updateInventoryStorageDim, res] = useMutation(
    UPDATE_INVENTORY_STORAGE_DIM_MUTATION,
    {
      refetchQueries: [
        "ALL_INVENTORY_STORAGE_DIMS_QUERY",
        "SINGLE_INVENTORY_STORAGE_DIM_QUERY"
      ]
    }
  );

  const handleSave = async e => {
    e.preventDefault();
    try {
      if (data && data.RecId) {
        await updateInventoryStorageDim({
          variables: {
            data: {
              DimCode: inputs.DimCode,
              DimDescription: inputs.DimDescription,
              InvStorageDimRecIdRef: parseInt(inputs.InvStorageDimRecIdRef),
              Active: inputs.Active,
              CompanyLocRecIdRef: parseInt(inputs.CompanyLocRecIdRef)
            },
            where: { RecId: parseInt(data.RecId) }
          }
        });
        toast.success("Inventory bin edited successfully.");
      } else {
        await createInventoryStorageDim({
          variables: {
            data: {
              ...inputs,
              CompanyLocRecIdRef: parseInt(inputs.CompanyLocRecIdRef),
              InvStorageDimRecIdRef: parseInt(inputs.InvStorageDimRecIdRef)
            }
          }
        });
        toast.success("Inventory bin created successfully.");
      }
      onComplete();
    } catch (err) {
      toast.error("Error creating Inventory Bin.");
    }
  };

  useEffect(() => {
    const getTransactionData = async () => {
      try {
        const { data: ItemTransactionData } = await client.query({
          query: ALL_ITEM_TRANSACTIONS_QUERY,
          variables: {
            where: {
              OR: [
                {
                  InvDim1RecIdRef: data?.RecId ? parseInt(data.RecId, 10) : null
                },
                {
                  InvDim2RecIdRef: data?.RecId ? parseInt(data.RecId, 10) : null
                },
                {
                  InvDim3RecIdRef: data?.RecId ? parseInt(data.RecId, 10) : null
                }
              ]
            },
            first: 1
          }
        });
        const transactions = ItemTransactionData?.ItemTransactions;

        if (Array.isArray(transactions) && transactions.length === 0) {
          setCanModify(true);
        }
      } catch (err) {
        console.log(err.message);
      }
    };
    if (data?.RecId) {
      getTransactionData();
    }
  }, [client, data]);

  const handleDelete = async e => {
    e.preventDefault();
    try {
      if (canModify) {
        const performDelete = async () => {
          setModalOpen(false, "");
          await client.mutate({
            mutation: DELETE_INVENTORY_STORAGE_DIM_MUTATION,
            variables: {
              where: {
                RecId: parseInt(data.RecId)
              }
            },
            refetchQueries: [
              "ALL_INVENTORY_STORAGE_DIMS_QUERY",
              "SINGLE_INVENTORY_STORAGE_DIM_QUERY"
            ]
          });
          toast.success("Inventory Storage Dimension deleted successfully.");
        };
        setModalOpen(
          true,
          <Confirmation
            message="Are you sure you want to delete?"
            onConfirm={performDelete}
          />
        );
      } else {
        toast.warning(
          "This Storage Dimension is currently being used but may be made inactive."
        );
      }
    } catch (err) {
      toast.error("Error deleting Inventory Storage Dimension.");
    }
  };

  return (
    <form
      className="add-item-form"
      id="add-item-form"
      style={{ width: 600, maxWidth: "100%" }}
      onSubmit={handleSave}
    >
      <Title>{data?.RecId ? "Edit" : "Create"} Storage Dimension</Title>
      <Fieldset disabled={loading || res.loading}>
        <InventoryStorageDimForm
          canModify={canModify}
          editing={editing}
          inputs={inputs}
          onChange={handleChange}
        />
        <hr />
        <Field kind="group">
          <Control expanded>
            <Field kind="group">
              <Control>
                <Button color="success" form="add-item-form" type="submit">
                  Save
                </Button>
              </Control>
              <Control>
                <Button type="button" onClick={onComplete}>
                  Cancel
                </Button>
              </Control>
            </Field>
          </Control>
          {data?.RecId && (
            <Control>
              <Button color="danger" onClick={handleDelete}>
                Delete
              </Button>
            </Control>
          )}
        </Field>
      </Fieldset>
    </form>
  );
};

AddInventoryStorageDimForm.propTypes = {
  onComplete: PropTypes.func,
  data: PropTypes.object
};
export default AddInventoryStorageDimForm;
